import type { GetThesisRow } from '../generated/openapi-types.gen.js';

/**
 * `applyThesisReactionOptimistic` — optimistic-cache helper for the
 * `useSetThesisReaction` / `useClearThesisReaction` mutations.
 *
 * Patches the thesis row matching `vars.signature` the moment the user clicks,
 * before the network round-trip:
 *   • `viewerReaction` flips to the new value so the up/down arrow turns
 *     red/green instantly.
 *   • `rating` moves by the signed delta implied by the transition
 *     (prev `viewerReaction` → next) so the score changes instantly too.
 *
 * Rating is authoritative from the backend; the mutation's `onSettled`
 * invalidates the target query, so the canonical value replaces this optimistic
 * value once the write settles. Because the helper computes a prev→next delta
 * off the CURRENT cached row (not a running local counter),
 * consecutive clicks compose correctly and never double-count — mirroring
 * `applyProposalVoteOptimistic`.
 *
 * Handles two cache shapes, since the same recipe targets both:
 *   • `useGetThesis`  (kind=single)   → `prev` IS the row.
 *   • `useGetTheses`  (kind=multiple) → `prev` is `PagedData<Row>`:
 *     `{ data: Row[], nextCursor, hasMore, _sort? }` — flat, shared by the
 *     `useGetThesesInfinite` feed.
 *
 * `vars.kind` discriminates the action:
 *   • `'up'` / `'down'` → set viewerReaction to that kind (useSetThesisReaction).
 *   • `undefined`       → clear viewerReaction to null (useClearThesisReaction —
 *     vars carries only `signature`, no `kind`).
 *
 * Immutable: returns the exact `prev` reference when no row matched so
 * structural-sharing short-circuits stay intact. Platform-agnostic — pure
 * data, no web/RN API. Safe in core.
 */

// `null` means the viewer is unauthenticated or has no reaction row (never reacted or cleared it).
type ReactionValue = GetThesisRow['viewerReaction'];

interface ThesisRowLike {
  readonly signature?: string;
  readonly viewerReaction?: ReactionValue;
  readonly rating: number;
}

interface PagedDataLike<Row> {
  readonly data?: ReadonlyArray<Row>;
}

function nextReactionFrom(vars: Record<string, unknown>): ReactionValue {
  const kind = vars.kind;
  if (kind === 'up' || kind === 'down') return kind;
  return null;
}

function reactionValue(reaction: ReactionValue): number {
  if (reaction === 'up') return 1;
  if (reaction === 'down') return -1;
  return 0;
}

function hasNumericRating(value: object): value is ThesisRowLike {
  return 'rating' in value && typeof value.rating === 'number';
}

/**
 * Apply the prev→next transition to one row. Returns the same reference when
 * the row already holds `nextReaction` (no-op) so structural sharing is kept.
 */
function patchRow<Row extends ThesisRowLike>(row: Row, nextReaction: ReactionValue): Row {
  const prevReaction: ReactionValue = row.viewerReaction ?? null;
  if (prevReaction === nextReaction) return row;
  return {
    ...row,
    viewerReaction: nextReaction,
    rating: row.rating + reactionValue(nextReaction) - reactionValue(prevReaction),
  };
}

function patchRowsBySignature<Row extends ThesisRowLike>(
  rows: ReadonlyArray<Row>,
  signature: string,
  nextReaction: ReactionValue,
): { rows: ReadonlyArray<Row>; changed: boolean } {
  let changed = false;
  const next = rows.map((row) => {
    if (row === null || row === undefined || row.signature !== signature) return row;
    const patched = patchRow(row, nextReaction);
    if (patched !== row) changed = true;
    return patched;
  });
  return { rows: next, changed };
}

export function applyThesisReactionOptimistic<T>(prev: T, vars: Record<string, unknown>): T {
  if (prev === null || prev === undefined || typeof prev !== 'object') return prev;
  const signature = vars.signature;
  if (typeof signature !== 'string' || signature.length === 0) return prev;
  const nextReaction = nextReactionFrom(vars);

  // Single-row shape — `prev` is a thesis row with `signature` + `viewerReaction`.
  if (hasNumericRating(prev) && prev.signature === signature) {
    const patched = patchRow(prev, nextReaction);
    return patched === prev ? prev : patched;
  }

  // PagedData shape — flat `{ data: Row[], … }`. Find row by signature, patch.
  if ('data' in prev && Array.isArray((prev as PagedDataLike<ThesisRowLike>).data)) {
    const envelope = prev as PagedDataLike<ThesisRowLike>;
    const rows = envelope.data as ReadonlyArray<ThesisRowLike>;
    const { rows: nextRows, changed } = patchRowsBySignature(rows, signature, nextReaction);
    if (!changed) return prev;
    return { ...(prev as object), data: nextRows } as T;
  }

  return prev;
}
